/**
 * preload.config.ts - Configuration for preloading modules
 * 
 * CHANGE HISTORY:
 * - 251110_1412 danielbeer: Created preload configuration system
 * 
 * Modules listed here will be preloaded in the background after initial render
 * to improve perceived performance for commonly used tools.
 */

import type { ToolType } from '../types';

export interface PreloadConfig {
  /**
   * Tools to preload immediately after app initialization
   * These load in the background while user is on home screen
   */
  immediate: ToolType[];
  
  /**
   * Tools to preload on user interaction (hover, focus)
   * These load when user shows intent to navigate
   */
  onIntent: ToolType[];
  
  /**
   * Tools to never preload (load only when explicitly requested)
   * Use for heavy/rarely used tools
   */
  lazy: ToolType[];
}

export const preloadConfig: PreloadConfig = {
  // Preload these immediately after app loads
  immediate: [
    'file-loader',      // Most common entry point
    'tag-manager',      // Frequently used
    'picker',           // Lightweight, commonly used
  ],
  
  // Preload on hover/intent
  onIntent: [
    'table-viewer',
    'transform-distill',
    'filemaker-bridge',
    'field-matcher',
  ],
  
  // Never preload (heavy or rarely used)
  lazy: [
    'pdf-viewer',       // Heavy library
    'office-viewer',    // Heavy library
    'setup-wizard',     // One-time use
    'documentation',    // Reference only
    'selectr-examples', // Demo only
  ],
};

/**
 * Get preload strategy for a tool
 */
export function getPreloadStrategy(tool: ToolType): 'immediate' | 'onIntent' | 'lazy' {
  if (preloadConfig.immediate.includes(tool)) return 'immediate';
  if (preloadConfig.onIntent.includes(tool)) return 'onIntent';
  if (preloadConfig.lazy.includes(tool)) return 'lazy';
  return 'onIntent'; // Default strategy
}

/**
 * Check if tool should be preloaded
 */
export function shouldPreload(tool: ToolType): boolean {
  return !preloadConfig.lazy.includes(tool);
}
